import React, { useState } from 'react';
import { api } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

export default function ContactPage() {
  const { locale, t } = useLanguage();
  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSending(true);

    try {
      await api.post('/contact', form);
      setSent(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err: any) {
      setError(err.message || 'Failed to send message. Please try again or email us directly.');
    } finally {
      setSending(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--gray-300)',
    fontSize: '0.95rem',
    transition: 'border-color 0.2s',
    outline: 'none',
    background: 'var(--white)',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.88rem',
    fontWeight: 600,
    color: 'var(--gray-700)',
    marginBottom: 6,
  };

  const faqs = [
    { qKey: 'contact.faq1q', aKey: 'contact.faq1a' },
    { qKey: 'contact.faq2q', aKey: 'contact.faq2a' },
    { qKey: 'contact.faq3q', aKey: 'contact.faq3a' },
    { qKey: 'contact.faq4q', aKey: 'contact.faq4a' },
    { qKey: 'contact.faq5q', aKey: 'contact.faq5a' },
  ];

  return (
    <div>
      {/* Hero */}
      <section
        style={{
          background: 'linear-gradient(135deg, #7B1FA2 0%, #4A148C 100%)',
          color: '#fff',
          padding: '100px 0 60px',
        }}
      >
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '2.8rem', fontWeight: 900, marginBottom: 12 }}>{t('contact.heroTitle')}</h1>
          <p style={{ fontSize: '1.15rem', opacity: 0.9, maxWidth: 600, margin: '0 auto' }}>
            {t('contact.heroSubtitle')}
          </p>
        </div>
      </section>

      <section style={{ padding: 'var(--section-padding)', background: 'var(--gray-100)' }}>
        <div
          className="container"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 48,
            maxWidth: 1100,
          }}
        >
          {/* Contact Info */}
          <div>
            <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--gray-900)', marginBottom: 8 }}>
              {t('contact.getInTouch')}
            </h2>
            <p style={{ fontSize: '0.95rem', color: 'var(--gray-600)', lineHeight: 1.6, marginBottom: 32 }}>
              {t('contact.haveQuestions')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: 'var(--red-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    flexShrink: 0,
                  }}
                >
                  &#9993;
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--gray-800)', marginBottom: 2 }}>{t('contact.email')}</div>
                  <a href="mailto:info@caaoc.org" style={{ color: 'var(--red-primary)', fontSize: '0.95rem' }}>
                    info@caaoc.org
                  </a>
                  <p style={{ fontSize: '0.83rem', color: 'var(--gray-500)', marginTop: 2 }}>
                    {t('contact.respondTime')}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: 'var(--blue-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    flexShrink: 0,
                  }}
                >
                  &#9742;
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--gray-800)', marginBottom: 2 }}>{t('contact.phone')}</div>
                  <a href="tel:+17146603180" style={{ color: 'var(--blue-accent)', fontSize: '0.95rem' }}>
                    (714) 660-3180
                  </a>
                  <p style={{ fontSize: '0.83rem', color: 'var(--gray-500)', marginTop: 2 }}>
                    {t('contact.phoneTimes')}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: 'var(--green-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    flexShrink: 0,
                  }}
                >
                  &#128205;
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--gray-800)', marginBottom: 2 }}>{t('contact.location')}</div>
                  <p style={{ color: 'var(--gray-700)', fontSize: '0.95rem' }}>
                    {t('contact.orangeCounty')}
                  </p>
                  <p style={{ fontSize: '0.83rem', color: 'var(--gray-500)', marginTop: 2 }}>
                    {t('contact.eventsVenues')}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: 'var(--yellow-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    flexShrink: 0,
                  }}
                >
                  &#128338;
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--gray-800)', marginBottom: 2 }}>{t('contact.officeHours')}</div>
                  <p style={{ color: 'var(--gray-700)', fontSize: '0.95rem' }}>
                    {t('contact.monFri')}
                  </p>
                  <p style={{ fontSize: '0.83rem', color: 'var(--gray-500)', marginTop: 2 }}>
                    {t('contact.weekends')}
                  </p>
                </div>
              </div>
            </div>

            {/* Social links placeholder */}
            <div style={{ marginTop: 32 }}>
              <div style={{ fontWeight: 600, color: 'var(--gray-800)', marginBottom: 12 }}>{t('contact.followUs')}</div>
              <div style={{ display: 'flex', gap: 12 }}>
                {['Facebook', 'WeChat', 'Instagram', 'YouTube'].map((platform) => (
                  <div
                    key={platform}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'var(--gray-200)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'var(--gray-600)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    title={platform}
                  >
                    {platform.substring(0, 2).toUpperCase()}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div
            className="card"
            style={{
              padding: 32,
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            {sent ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'var(--green-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    margin: '0 auto 20px',
                  }}
                >
                  &#10003;
                </div>
                <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--gray-900)', marginBottom: 8 }}>
                  {t('contact.messageSent')}
                </h3>
                <p style={{ color: 'var(--gray-600)', marginBottom: 24 }}>
                  {t('contact.thankYou')}
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="btn btn-outline"
                >
                  {t('contact.sendAnother')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--gray-900)', marginBottom: 24 }}>
                  {t('contact.sendUsMessage')}
                </h3>

                {error && (
                  <div
                    style={{
                      background: 'var(--red-bg)',
                      color: 'var(--red-primary)',
                      padding: '12px 16px',
                      borderRadius: 'var(--radius-sm)',
                      marginBottom: 20,
                      fontSize: '0.9rem',
                    }}
                  >
                    {error}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>
                      {t('contact.fullName')} <span style={{ color: 'var(--red-primary)' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.yourName')}
                      style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = 'var(--red-primary)')}
                      onBlur={(e) => (e.target.style.borderColor = 'var(--gray-300)')}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>
                      {t('contact.email')} <span style={{ color: 'var(--red-primary)' }}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.yourEmail')}
                      style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = 'var(--red-primary)')}
                      onBlur={(e) => (e.target.style.borderColor = 'var(--gray-300)')}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>{t('contact.phone')}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder={t('contact.phonePlaceholder')}
                      style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = 'var(--red-primary)')}
                      onBlur={(e) => (e.target.style.borderColor = 'var(--gray-300)')}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>
                      {t('contact.subject')} <span style={{ color: 'var(--red-primary)' }}>*</span>
                    </label>
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      required
                      style={{ ...inputStyle, cursor: 'pointer' }}
                      onFocus={(e) => (e.target.style.borderColor = 'var(--red-primary)')}
                      onBlur={(e) => (e.target.style.borderColor = 'var(--gray-300)')}
                    >
                      <option value="">{t('contact.selectTopic')}</option>
                      <option value="general">{t('contact.generalInquiry')}</option>
                      <option value="membership">{t('contact.membership')}</option>
                      <option value="events">{t('contact.events')}</option>
                      <option value="volunteering">{t('contact.volunteering')}</option>
                      <option value="sponsorship">{t('contact.sponsorship')}</option>
                      <option value="education">{t('contact.educationPrograms')}</option>
                      <option value="other">{t('contact.other')}</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <label style={labelStyle}>
                    {t('contact.message')} <span style={{ color: 'var(--red-primary)' }}>*</span>
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder={t('contact.howCanWeHelp')}
                    style={{ ...inputStyle, resize: 'vertical' }}
                    onFocus={(e) => (e.target.style.borderColor = 'var(--red-primary)')}
                    onBlur={(e) => (e.target.style.borderColor = 'var(--gray-300)')}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={sending}
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    padding: '14px 24px',
                    fontSize: '1rem',
                    opacity: sending ? 0.7 : 1,
                  }}
                >
                  {sending ? t('contact.sending') : t('contact.sendMessage')}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: 'var(--section-padding)', background: 'var(--white)' }}>
        <div className="container" style={{ maxWidth: 800 }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 className="section-title">{t('contact.faq')}</h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {faqs.map((faq, i) => (
              <div
                key={i}
                style={{
                  background: 'var(--gray-100)',
                  borderRadius: 'var(--radius-md)',
                  padding: '20px 24px',
                }}
              >
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--gray-900)', marginBottom: 8 }}>
                  {t(faq.qKey)}
                </h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--gray-600)', lineHeight: 1.6 }}>
                  {t(faq.aKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .container[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
